import React from 'react';
import { PlanBoardItem } from 'types';
import PlanBoardCard from './PlanBoardCard';
import { dragSortItemClassName, dragSortTranslateStyle, getDragSortTranslateY } from 'utils/dragSortList';

/** Fallback height (px) for the drop-insertion placeholder when no existing card can be measured. */
export const PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT = 96;

/** Matches `.plan-column__list`'s own CSS `gap` (app.css) — see getDragSortTranslateY's doc comment. */
const PLAN_LIST_GAP_PX = 8;

/** The live from→to snapshot of a drag that started in THIS column and is still hovering it —
 * see PlanPage's own doc comment on `isSameListReorder`/`planDragSortState`/`triageDragSortState`
 * for why this is a narrower condition than "a card from this list is being dragged"
 * (dragOverInsertIndex/elevatedItemId/draggingItemId below already cover the canvas-bound cases
 * this doesn't). */
export type PlanBoardColumnDragSortState = {
    fromIndex: number;
    toIndex: number;
    cardHeight: number;
};

type PlanBoardColumnProps = {
    /** Unplaced items, in display order. */
    items: PlanBoardItem[];
    /** Message shown in place of the list when `items` is empty (and no drop is in progress). */
    emptyMessage: string;
    /**
     * Item pressed and/or currently being dragged (while its origin is this list) — shown
     * elevated in place rather than removed/hidden, so the list's layout doesn't shift until
     * the drag actually resolves (see PlanPage's listElevatedItemId).
     */
    elevatedItemId?: string | null;
    /** Item actually mid-drag (past the movement threshold, ghost already floating) — dimmed in place to read as "lifted out", distinct from the pre-threshold elevated-only press. Ignored for the item named by `dragSortState`, which uses the same hide+slide-siblings treatment as the Insights/Triage panels instead (see dragSortState below). */
    draggingItemId?: string | null;
    /** Item currently toggled selected (see PlanPage's selectedItemId) — shown with a highlight outline. */
    selectedItemId?: string | null;
    /** Called when the list's empty background (not a card) is clicked, to clear the current selection. */
    onBackgroundClick?: () => void;
    /**
     * Index (within `items`) where a drop placeholder gap should render. Only meaningful
     * while a card is being dragged in FROM the Timeline canvas — reordering within this
     * list uses `dragSortState` below instead.
     */
    dragOverInsertIndex?: number | null;
    /** Height (px) for the placeholder, measured from an existing card by the caller; falls back to PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT. */
    dragOverInsertHeight?: number | null;
    /**
     * Non-null exactly while a card that started in THIS list is still being dragged over it —
     * a same-list reorder, the one case this column's cards get a live position preview for.
     * Drives the exact same "hide the dragged card, slide the cards between its old and new
     * slot out of the way" treatment as InsightsPanel/TriagePanel (see utils/dragSortList.ts) —
     * `fromIndex`/`toIndex` index into `items` the same way those panels' own drag state does.
     */
    dragSortState?: PlanBoardColumnDragSortState | null;
    listRef: React.RefObject<HTMLDivElement | null>;
    onCardMouseDown: (e: React.MouseEvent, itemId: string) => void;
};

/**
 * A Plan page list column: every item (story/experiment, or triage — see PlanBoardItem, types.ts)
 * that hasn't been placed on the Timeline grid, rendered as draggable PlanBoardCards. Shared by
 * PlanPage's Plan and Triage columns so both read as the exact same list/card styling and
 * drag-to-reorder/drag-to-canvas behavior — only the item source and empty-state copy differ
 * between the two callers. Drag mechanics (reordering within this list, and handing off to the
 * Timeline canvas) are owned by PlanPage — this component only renders the list and forwards the
 * initiating mousedown.
 */
export default function PlanBoardColumn({ items, emptyMessage, elevatedItemId, draggingItemId, selectedItemId, onBackgroundClick, dragOverInsertIndex, dragOverInsertHeight, dragSortState, listRef, onCardMouseDown }: PlanBoardColumnProps) {
    const placeholderHeight = dragOverInsertHeight ?? PLAN_BOARD_COLUMN_PLACEHOLDER_HEIGHT;

    const handleListClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onBackgroundClick?.();
    };

    return (
        <div ref={listRef} className='plan-column__list' onClick={handleListClick}>
            {items.length === 0 && dragOverInsertIndex == null && (
                <p className='plan-column__empty'>{emptyMessage}</p>
            )}

            {items.map((item, index) => {
                // While reordering within this list, every card (not just the dragged one)
                // gets the shared draggable-list-item treatment — including the ones that
                // aren't actually shifting (translateY 0), so the CSS transition class applies
                // uniformly and a card doesn't visually "snap" the instant it starts/stops moving.
                const isBeingDraggedHere = dragSortState ? index === dragSortState.fromIndex : false;
                const translateY = dragSortState ? getDragSortTranslateY(dragSortState, index, PLAN_LIST_GAP_PX) : 0;

                return (
                    <React.Fragment key={item.id}>
                        {dragOverInsertIndex === index && (
                            <div className='plan-column__drop-placeholder' style={{ height: placeholderHeight }} />
                        )}
                        <div
                            data-plan-item-id={item.id}
                            className={
                                dragSortState
                                    ? dragSortItemClassName('plan-column__item', true, isBeingDraggedHere)
                                    : `plan-column__item ${item.id === draggingItemId ? 'plan-column__item--dragging' : ''}`.trim()
                            }
                            style={dragSortState ? dragSortTranslateStyle(translateY) : undefined}
                        >
                            <PlanBoardCard
                                item={item}
                                variant='list'
                                elevated={item.id === elevatedItemId}
                                selected={item.id === selectedItemId}
                                onCardMouseDown={(e) => onCardMouseDown(e, item.id)}
                            />
                        </div>
                    </React.Fragment>
                );
            })}

            {dragOverInsertIndex === items.length && (
                <div className='plan-column__drop-placeholder' style={{ height: placeholderHeight }} />
            )}
        </div>
    );
}
